"use client";

import Image from "next/image";
import Button from "@/components/ui/Button/Button";
import { Highlighter } from "@/components/ui/highlighter";
import { useTranslations } from "next-intl";
import { useCallback } from "react";
import { useSession } from "next-auth/react";

const CALL_URLS = {
    accelerators: "https://go.believemy.com/appel-info",
    rocket: "https://go.believemy.com/appel-decouverte-sur-rocket",
    python: "https://go.believemy.com/appel-decouverte-python",
};

export default function BookNewCall({
    shouldDirectLinkToCall = false,
    type = "rocket",
    onCtaClick,
}) {
    const t = useTranslations("BookNewCall");
    const { data: session } = useSession();

    const callUrl = CALL_URLS[type] || CALL_URLS.rocket;

    // Fonction réutilisable pour le smooth scroll
    const handleSmoothScroll = useCallback(
        (e, targetId) => {
            e.preventDefault();

            const targetElement = document.getElementById(targetId);
            if (!targetElement) return;

            // Pour les utilisateurs connectés, chercher TOUS les éléments scrollables possibles
            const scrollContainers = document.querySelectorAll(
                '.overflow-y-auto, .overflow-auto, [style*="overflow"]',
            );

            let scrolled = false;

            // Essayer de trouver le bon container
            for (const container of scrollContainers) {
                // Vérifier si le container contient l'élément cible
                if (
                    container.contains(targetElement) &&
                    container.scrollHeight > container.clientHeight
                ) {
                    // Calculer la position
                    const offset = session ? 125 : 72; // 65px header + 60px nav pour connectés, 72px pour non-connectés
                    const targetRect = targetElement.getBoundingClientRect();
                    const containerRect = container.getBoundingClientRect();

                    const scrollPosition =
                        container.scrollTop +
                        targetRect.top -
                        containerRect.top -
                        offset;

                    container.scrollTo({
                        top: scrollPosition,
                        behavior: "smooth",
                    });

                    scrolled = true;
                    break;
                }
            }

            // Si aucun container trouvé, essayer avec window
            if (!scrolled) {
                const elementPosition =
                    targetElement.getBoundingClientRect().top;
                const offsetPosition =
                    elementPosition + window.scrollY - (session ? 125 : 72);

                window.scrollTo({
                    top: offsetPosition,
                    behavior: "smooth",
                });
            }
        },
        [session],
    );
    return (
        <div className="py-36 relative overflow-hidden">
            <div className="w-[35%] absolute md:block hidden top-[10%] -left-[17%] aspect-1">
                <Image src="/images/site/assets/shape.png" alt="Shape" fill />
            </div>
            <div className="w-[35%] absolute md:block hidden top-[10%] -right-[17%] aspect-1">
                <Image src="/images/site/assets/shape.png" alt="Shape" fill />
            </div>
            <div className="m-container z-10">
                <div className="md:w-[550px] mx-auto">
                    <div className="flex md:justify-center justify-start mb-6">
                        <div className="inline-flex items-center gap-2 text-primary-100 px-4 py-2 bg-primary-100/10 rounded-full backdrop-blur-sm">
                            <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                            </svg>
                            <span className="text-sm font-medium uppercase tracking-wider">
                                {t("ready_to_start")}
                            </span>
                        </div>
                    </div>
                    <h2 className="md:text-6xl text-4xl md:text-center text-left mx-auto font-title dark:text-white">
                        {t("discuss_your_project")}{" "}
                        <Highlighter
                            action="box"
                            color="#f04bcc"
                            strokeWidth={1.9}
                            animationDuration={1000}
                            isView
                        >
                            {t("with_nicolas")}
                        </Highlighter>
                    </h2>
                    <div className="text-lg font-light mt-10 leading-relaxed text-black/90 md:text-center md:w-4/5 mx-auto dark:text-lightBlue">
                        {t("book_a_call")}{" "}
                        <Highlighter
                            action="highlight"
                            color="#e9edf0"
                            darkColor="#38444D"
                            isView
                        >
                            {t("free")}
                        </Highlighter>{" "}
                        {t("of_30_minutes")}{" "}
                        <Highlighter
                            action="highlight"
                            color="#e9edf0"
                            darkColor="#38444D"
                            isView
                        >
                            {t("no_commitment")}
                        </Highlighter>
                    </div>
                    <div className="mt-10 flex md:justify-center">
                        {onCtaClick ? (
                            <div onClick={onCtaClick} className="cursor-pointer">
                                <Button type="cta">
                                    {t("book_call_with_nicolas")}
                                    <div className="ml-3 w-[30px] h-[30px] rounded-full overflow-hidden">
                                        <Image
                                            src="/images/site/people/lnl.jpg"
                                            alt="Nicolas"
                                            width={35}
                                            height={35}
                                        />
                                    </div>
                                </Button>
                            </div>
                        ) : (
                            <a
                                href={
                                    shouldDirectLinkToCall
                                        ? callUrl
                                        : "#get-program"
                                }
                                onClick={(e) =>
                                    shouldDirectLinkToCall
                                        ? null
                                        : handleSmoothScroll(e, "get-program")
                                }
                                target={shouldDirectLinkToCall ? "_blank" : "_self"}
                            >
                                <Button type="cta">
                                    {t("book_call_with_nicolas")}
                                    <div className="ml-3 w-[30px] h-[30px] rounded-full overflow-hidden">
                                        <Image
                                            src="/images/site/people/lnl.jpg"
                                            alt="Nicolas"
                                            width={35}
                                            height={35}
                                        />
                                    </div>
                                </Button>
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
